<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { useProjectUserStore } from '@/stores/projectUser'
import { useAuthorizationStore } from '@/stores/authorization'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useProjectStore } from '@/stores/project'

import type { ProjectUserType } from '@/stores/type/projectUser.type'

import { UserManagement } from '@haskoning/vue-component-library'

const projectStore = useProjectStore()
const projectUserStore = useProjectUserStore()
const authorizationStore = useAuthorizationStore()
const authenticationStore = useDjangoAuthStore()

const { currentItem: currentProject } = storeToRefs(projectStore)
const { user: currentUser, searchResults, searchLoading } = storeToRefs(authenticationStore)
const { items: roles } = storeToRefs(authorizationStore)
const {
  items: projectUserItems,
  addUserDialog,
  writeLoading,
  deleteDialog,
  deleteLoading,
  deleteItem
} = storeToRefs(projectUserStore)

const canAdd = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['add_projectuser']
  })
})

const canEdit = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['change_projectuser']
  })
})

const handleDelete = (item: ProjectUserType) => {
  projectUserStore.setDeleteItem(item)
  projectUserStore.openDeleteDialog()
}

const changeRole = async (payload: { item: ProjectUserType; newRole: number }) => {
  const { item, newRole } = payload
  if (item && newRole) {
    await projectUserStore.updateRole('project', item, newRole)
  }
}

const addUser = async (userId: number) => {
  if (!currentProject.value) return
  await projectUserStore.addUser('project', currentProject.value.id, userId)
}

const searchUsers = async (search: string) => {
  if (!currentProject.value) return
  await authenticationStore.searchUsers('project', currentProject.value.id, search)
}
</script>

<template>
  <div class="px-8 py-6">
    <UserManagement
      v-if="currentUser"
      :can-add="canAdd"
      :can-edit="canEdit"
      :current-user="currentUser"
      :delete-item="deleteItem"
      :roles="roles"
      :auth-model-users="projectUserItems"
      auth-role="ProjectUser"
      :search-results="searchResults"
      :add-loading="writeLoading"
      :search-loading="searchLoading"
      :delete-loading="deleteLoading"
      v-model:addUserDialog="addUserDialog"
      v-model:deleteDialog="deleteDialog"
      @add-user="addUser"
      @search-users="searchUsers"
      @reset-state="authenticationStore.resetSearchState()"
      @handleDelete="handleDelete"
      @set-selected-item="deleteItem = $event"
      @change-role="changeRole"
      @delete-item="projectUserStore.delete($event)"
    />
  </div>
</template>
